import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  OnInit
} from '@angular/core';
import { SensorService } from '@app/core/sensor.service';
import { ModuloService } from '@app/core/modulo.service';
import { ClientService } from '@app/core/client.service';

interface SmartTableActions {
  add?: boolean;
  edit?: boolean;
  delete?: boolean;
  columnTitle?: string;
  alarmFinca?: boolean;
}

interface SmartTableSettings {
  columns: Record<
    string,
    { title?: string; editable?: boolean; filter?: boolean; sort?: boolean }
  >;
  actions?: boolean | SmartTableActions;
  pager?: {
    display: boolean;
    perPage: number;
  };
}

@Component({
  standalone: false,
  selector: 'app-smart-table',
  template: `
    <table class="smart-table mat-elevation-z1">
      <thead>
        <tr *ngIf="columnKeys.length">
          <th *ngFor="let col of columnKeys">
            {{ settings.columns[col]?.title || col }}
          </th>
          <th *ngIf="showActions">
            {{ settings.actions?.columnTitle || 'Acciones' }}
          </th>
        </tr>
        <tr *ngIf="columnKeys.length">
          <th *ngFor="let col of columnKeys">
            <mat-form-field
              *ngIf="settings.columns[col]?.filter !== false"
              appearance="outline"
              floatLabel="never"
              class="filter-field"
            >
              <input
                matInput
                type="text"
                [value]="filterValues[col] || ''"
                (input)="onFilterChange(col, $any($event.target).value)"
                [placeholder]="settings.columns[col]?.title || col"
              />
            </mat-form-field>
          </th>
          <th *ngIf="showActions"></th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let row of rows">
          <td *ngFor="let col of columnKeys">
            {{ getDisplayValue(col, row) }}
          </td>
          <td *ngIf="showActions" class="actions-cell">
            <button
              mat-icon-button
              *ngIf="canToggle"
              aria-label="Toggle"
              (click)="emitToggle(row)"
              [color]="row?.isEnabled === false ? 'warn' : 'primary'"
            >
              <mat-icon>power_settings_new</mat-icon>
            </button>
            <button
              mat-icon-button
              *ngIf="canAlarmFinca"
              [disabled]="row?.alarmLoading || row?.isDeleted"
              [color]="row?.alarmSubscribed ? 'primary' : undefined"
              [attr.aria-label]="
                row?.alarmSubscribed
                  ? ('ALARM_FARMS.UNSUBSCRIBE' | translate)
                  : ('ALARM_FARMS.SUBSCRIBE' | translate)
              "
              (click)="emitAlarmFinca(row)"
            >
              <mat-icon>{{
                row?.alarmSubscribed ? 'notifications_active' : 'notifications_off'
              }}</mat-icon>
            </button>
            <button
              mat-icon-button
              color="primary"
              *ngIf="canEdit"
              aria-label="Editar"
              (click)="emitEdit(row)"
            >
              <mat-icon>edit</mat-icon>
            </button>
            <button
              mat-icon-button
              color="warn"
              *ngIf="canDelete"
              aria-label="Eliminar"
              (click)="emitDelete(row)"
            >
              <mat-icon>delete</mat-icon>
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  `,
  styles: [
    `
      .smart-table {
        width: 100%;
        border-collapse: collapse;
        color: var(--app-text);
        background: var(--app-surface);
        border: 1px solid var(--app-border);
        border-radius: 12px;
        overflow: hidden;
      }
      .smart-table thead th {
        font-weight: 600;
        padding: 12px;
        border-bottom: 1px solid var(--app-border);
        color: var(--app-muted);
        background: var(--app-surface);
        text-align: center;
      }
      .smart-table thead th:not(:last-child) {
        border-right: 1px solid var(--app-border);
      }
      .smart-table tbody td {
        padding: 12px;
        border-bottom: 1px solid var(--app-border);
        text-align: center;
      }
      .smart-table tbody td:not(:last-child) {
        border-right: 1px solid var(--app-border);
      }
      .actions-cell {
        display: flex;
        justify-content: center;
        gap: 8px;
        align-items: center;
      }
      .smart-table tbody tr:nth-child(odd) {
        background: var(--app-surface-2);
      }
      .smart-table tbody tr:hover {
        background: var(--app-surface-2);
      }
      .filter-field {
        width: 100%;
      }
    `
  ]
})
export class SmartTableComponent implements OnChanges, OnInit {
  @Input() settings: SmartTableSettings;
  @Input() source: any[] = [];

  @Output() edit = new EventEmitter<any>();
  @Output() delete = new EventEmitter<any>();
  @Output() create = new EventEmitter<any>();
  @Output() editConfirm = new EventEmitter<any>();
  @Output() deleteConfirm = new EventEmitter<any>();
  @Output() createConfirm = new EventEmitter<any>();
  @Output() toggle = new EventEmitter<any>();
  @Output() alarmFinca = new EventEmitter<any>();

  filterValues: Record<string, string> = {};
  private filteredSource: any[] = [];
  private clients: any[] = [];
  private modules: any[] = [];
  constructor(
    private sensorService: SensorService,
    private moduleService: ModuloService,
    private clientService: ClientService
  ) {}

  ngOnInit(): void {
    this.clientService.data$.subscribe(data => (this.clients = data || []));
    this.moduleService.data$.subscribe(data => (this.modules = data || []));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['source']) {
      this.applyFilters();
    }
  }

  get rows(): any[] {
    return this.filteredSource || [];
  }

  get columnKeys(): string[] {
    return this.settings?.columns ? Object.keys(this.settings.columns) : [];
  }

  get showActions(): boolean {
    return this.settings?.actions !== false;
  }

  get canEdit(): boolean {
    return (this.settings?.actions as SmartTableActions)?.edit !== false;
  }

  get canDelete(): boolean {
    return (this.settings?.actions as SmartTableActions)?.delete !== false;
  }

  get canToggle(): boolean {
    // Custom toggle action (e.g. enable/disable modules)
    return (this.settings?.actions as any)?.toggle === true;
  }

  get canAlarmFinca(): boolean {
    return (this.settings?.actions as SmartTableActions)?.alarmFinca === true;
  }

  emitEdit(row: any) {
    const event = { data: row, newData: { ...row } };
    this.edit.emit(event);
    this.editConfirm.emit(event);
  }

  emitDelete(row: any) {
    const event = { data: row };
    this.delete.emit(event);
    this.deleteConfirm.emit(event);
  }

  emitToggle(row: any) {
    const event = { data: row };
    this.toggle.emit(event);
  }

  emitAlarmFinca(row: any) {
    const event = { data: row };
    this.alarmFinca.emit(event);
  }

  getDisplayValue(col: string, row: any): any {
    if (col === 'sensorType' && this.sensorService?.sensorTypesFixed) {
      const match = this.sensorService.sensorTypesFixed.find(
        (s: any) => s.value === row[col]
      );
      if (match) {
        return match.name;
      }
    }
    if (
      col === 'powerSupplyType' &&
      this.moduleService?.modulePowerSupplyTypes
    ) {
      const match = this.moduleService.modulePowerSupplyTypes.find(
        (p: any) => p.value === row[col]
      );
      if (match) {
        return match.label;
      }
    }
    if (col === 'idModule') {
      // In module config table, show the raw module id (name is already in its own column).
      if (row?.idModule !== undefined && row?.name !== undefined) {
        return row[col];
      }
      const moduleFromRow = row?.module;
      if (moduleFromRow?.name) {
        return moduleFromRow.name;
      }
      if (this.modules?.length) {
        const match = this.modules.find(
          (m: any) => String(m.idModule) === String(row[col])
        );
        if (match) {
          return match.name;
        }
      }
    }
    if (col === 'idCliente') {
      const clientFromRow = row?.cliente || row?.client;
      if (clientFromRow?.name) {
        return clientFromRow.name;
      }
      if (this.clients?.length) {
        const match = this.clients.find(
          (c: any) => String(c.idCliente) === String(row[col])
        );
        if (match) {
          return match.name;
        }
      }
    }
    return row[col];
  }

  onFilterChange(column: string, value: string) {
    this.filterValues = { ...this.filterValues, [column]: value || '' };
    this.applyFilters();
  }

  private applyFilters() {
    const src = this.source || [];
    const activeFilters = Object.entries(this.filterValues).filter(
      ([, v]) => !!v
    );
    if (!activeFilters.length) {
      this.filteredSource = [...src];
      return;
    }

    this.filteredSource = src.filter(row =>
      activeFilters.every(([col, val]) =>
        String(row[col] ?? '')
          .toLowerCase()
          .includes(val.toLowerCase())
      )
    );
  }
}
